import { getRscCacheDetails } from './rsc-cache-observer'
import { sendWebappLoadPerformance } from './load-performance'

export const GRID_PERFORMANCE_EVENT = 'webapp:grid-performance-start'
export const GRID_NAVIGATION_TIMEOUT_MS = 30_000

type GridMeasurement = {
  id: string
  startedAt: number
  origin: string
  destination?: string
  dataReadyAt?: number
  serverDataMs?: number
  serverPrepareMs?: number
  timeout: ReturnType<typeof setTimeout>
  onHidden: () => void
}

let pending: GridMeasurement | undefined

export function navigationPath(href: string): string {
  const url = new URL(href, window.location.href)
  return `${url.pathname.replace(/\/$/, '')}${url.search}`
}

export function getGridMeasurement() {
  return pending
}

export function cancelGridMeasurement() {
  if (!pending) return
  clearTimeout(pending.timeout)
  document.removeEventListener('visibilitychange', pending.onHidden)
  pending = undefined
}

export function cancelGridMeasurementOnRouteChange(pathname: string) {
  if (!pending || pathname === pending.origin) return
  const destinationPath = pending.destination ? new URL(pending.destination, window.location.href).pathname : undefined
  if (destinationPath !== pathname) cancelGridMeasurement()
}

export function startGridMeasurement(href?: string) {
  if (typeof window === 'undefined' || document.visibilityState === 'hidden') return
  const destination = href ? navigationPath(href) : undefined
  if (destination && destination === navigationPath(window.location.href)) return
  cancelGridMeasurement()
  const id = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${performance.now()}`
  const onHidden = () => {
    if (document.visibilityState === 'hidden') cancelGridMeasurement()
  }
  pending = {
    id,
    startedAt: performance.now(),
    origin: window.location.pathname,
    destination,
    onHidden,
    timeout: setTimeout(() => {
      if (pending?.id !== id) return
      const measurement = pending
      cancelGridMeasurement()
      sendWebappLoadPerformance({
        surface: 'pages',
        operation: 'grid_navigation',
        source: 'client',
        status: 'error',
        duration_ms: Math.round(performance.now() - measurement.startedAt),
        navigation_id: id,
        origin_path: measurement.origin,
        destination_path: measurement.destination ?? window.location.pathname,
        ...navigationDimensions(measurement.destination),
        error_type: 'grid_navigation_timeout',
      })
    }, GRID_NAVIGATION_TIMEOUT_MS),
  }
  document.addEventListener('visibilitychange', onHidden)
  window.dispatchEvent(new Event(GRID_PERFORMANCE_EVENT))
}

export function markGridDataReady(id: string, serverDataMs?: number, serverPrepareMs?: number) {
  if (pending?.id !== id || pending.dataReadyAt !== undefined) return
  pending.dataReadyAt = performance.now()
  pending.serverDataMs = serverDataMs
  pending.serverPrepareMs = serverPrepareMs
}

export function finishGridMeasurement(id: string, thumbnailsReady: boolean) {
  if (pending?.id !== id || pending.dataReadyAt === undefined) return
  const measurement = pending
  const endedAt = performance.now()
  const destination = measurement.destination
  const cacheDetails = getRscCacheDetails(destination ?? window.location.href, measurement.startedAt, endedAt)
  cancelGridMeasurement()
  sendWebappLoadPerformance({
    surface: 'pages',
    operation: 'grid_navigation',
    source: 'client',
    status: 'success',
    duration_ms: Math.round(endedAt - measurement.startedAt),
    navigation_id: id,
    origin_path: measurement.origin,
    destination_path: measurement.destination ?? window.location.pathname,
    ...navigationDimensions(measurement.destination),
    navigation_data_ms: Math.round(measurement.dataReadyAt! - measurement.startedAt),
    grid_presentation_ms: Math.round(endedAt - measurement.dataReadyAt!),
    thumbnails_ready: thumbnailsReady,
    ...cacheDetails,
    ...(measurement.serverDataMs !== undefined ? { server_data_ms: measurement.serverDataMs } : {}),
    ...(measurement.serverPrepareMs !== undefined ? { server_prepare_ms: measurement.serverPrepareMs } : {}),
  })
}

function navigationDimensions(destination?: string) {
  const url = new URL(destination ?? window.location.href, window.location.href)
  const [main, category, subCategory, content] = url.pathname.split('/').filter(Boolean)
  const vertical = ['vfx', 'sfx', 'graphics', '3d'].includes(main) ? main : url.searchParams.get('main_category')
  return vertical && ['vfx', 'sfx', 'graphics', '3d', 'all'].includes(vertical)
    ? { main_category: vertical, ...(main !== 'search' ? { category, sub_category: subCategory, content } : {}) }
    : {}
}
